// GtApply — the public apply entry point (jobs surface links here as /apply/:ref).
// The signed-out step reuses the established .gt-signin design verbatim (same
// signin-bg background, Generative Talent logo, tagline, Clerk card sizing) so it
// is visually identical to the internal sign-in, with a single "Applying for
// [role]" context line added. The signed-in step is the one-tap consent screen.
// Identity is never trusted from the client; the backend reads the verified email
// from the Clerk session at submit.
import { useState, useEffect, useCallback } from 'react';
import { SignedIn, SignedOut, SignIn, SignUp, useAuth } from '@clerk/clerk-react';
import API_BASE from '../../config';
import wordmarkBlack from '../../brand/main-logo-black.png';

function refFromPath() {
  const p = typeof window !== 'undefined' ? window.location.pathname : '';
  const m = p.match(/^\/apply\/([^/?#]+)/);
  return m ? m[1] : '';
}

export default function GtApply({ clerkAppearance }) {
  const roleRef = refFromPath();
  const returnUrl = typeof window !== 'undefined' ? window.location.pathname : '/';
  const [role, setRole] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      if (!roleRef) return;
      try {
        const r = await fetch(API_BASE + '/api/public/roles/' + encodeURIComponent(roleRef));
        if (r.ok) { const d = await r.json(); if (live) setRole(d); }
      } catch (e) { /* confirm step handles load errors */ }
    })();
    return () => { live = false; };
  }, [roleRef]);

  return (
    <>
      <SignedOut>
        <ApplyAuth returnUrl={returnUrl} role={role} clerkAppearance={clerkAppearance} />
      </SignedOut>
      <SignedIn>
        <div className="gtap-root">
          <div className="gtap-card">
            <img src={wordmarkBlack} alt="Generative Talent Co" className="gtap-logo" />
            <ApplyConfirm roleRef={roleRef} preloadedRole={role} />
          </div>
        </div>
      </SignedIn>
    </>
  );
}

// Signed-out: identical to the internal sign-in surface, plus a context line.
function ApplyAuth({ returnUrl, role, clerkAppearance }) {
  const [wantsSignUp, setWantsSignUp] = useState(true); // most applicants are new
  return (
    <div className="gt-signin">
      <div className="gt-signin-box">
        <img src={wordmarkBlack} alt="Generative Talent Co" className="gt-signin-wordmark gtap-signin-logo" />
        <p className="gt-signin-tagline">We recruit for what's next</p>
        <p className="gtap-applying-for">
          Applying for <strong>{role ? role.title : 'this role'}</strong>
        </p>
        {wantsSignUp ? (
          <SignUp routing="hash" appearance={clerkAppearance} afterSignUpUrl={returnUrl} afterSignInUrl={returnUrl} />
        ) : (
          <SignIn routing="hash" appearance={clerkAppearance} afterSignInUrl={returnUrl} afterSignUpUrl={returnUrl} />
        )}
        <p className="gt-signin-toggle">
          {wantsSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button type="button" onClick={() => setWantsSignUp(!wantsSignUp)}>
            {wantsSignUp ? 'Sign in' : 'Sign up'}
          </button>
        </p>
      </div>
    </div>
  );
}

// Signed-in: load the role, show one-tap confirmation with consent, submit.
function ApplyConfirm({ roleRef, preloadedRole }) {
  const { getToken } = useAuth();
  const [role, setRole] = useState(preloadedRole || null);
  const [loadState, setLoadState] = useState(preloadedRole ? 'ready' : 'loading');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [needCv, setNeedCv] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [retryApply, setRetryApply] = useState(false);

  useEffect(() => {
    if (preloadedRole) { setRole(preloadedRole); setLoadState('ready'); return; }
    let live = true;
    (async () => {
      if (!roleRef) { setLoadState('notfound'); return; }
      try {
        const r = await fetch(API_BASE + '/api/public/roles/' + encodeURIComponent(roleRef));
        if (r.status === 404) { if (live) setLoadState('notfound'); return; }
        if (!r.ok) { if (live) setLoadState('error'); return; }
        const data = await r.json();
        if (live) { setRole(data); setLoadState('ready'); }
      } catch (e) {
        if (live) setLoadState('error');
      }
    })();
    return () => { live = false; };
  }, [roleRef, preloadedRole]);

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const r = await fetch(API_BASE + '/api/public/apply/' + encodeURIComponent(roleRef), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 403 && data.error === 'email_unverified') {
        setError('Verify your email address with the link we sent, then apply again.');
        return;
      }
      if (r.status === 409 && data.error === 'role_closed') {
        setError('This role is no longer accepting applications.');
        return;
      }
      // Their account now exists but carries no CV. Ask for it in place.
      if (r.status === 409 && data.error === 'cv_required') {
        setNeedCv(true);
        setError(null);
        return;
      }
      if (!r.ok) {
        setError('Something went wrong completing your application. Try again shortly.');
        return;
      }
      setResult(data);
    } catch (e) {
      setError('Something went wrong completing your application. Try again shortly.');
    } finally {
      setSubmitting(false);
    }
  }, [getToken, roleRef]);

  // The apply call has already created their record, so /api/portal/cv works
  // even though they have never seen the portal. Upload, then finish the
  // application they already asked for rather than making them click twice.
  const uploadCvThenApply = useCallback(async (file) => {
    if (!file) return;
    setUploadingCv(true); setError(null);
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('cv', file);
      const r = await fetch(API_BASE + '/api/portal/cv', {
        method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(d.error === 'cv parse failed'
          ? 'We could not read that file. Try a different PDF or Word document.'
          : 'Could not upload your CV. Try again shortly.');
        return;
      }
      setNeedCv(false);
      setRetryApply(true);
    } catch (e) {
      setError('Could not upload your CV. Try again shortly.');
    } finally { setUploadingCv(false); }
  }, [getToken]);

  useEffect(() => {
    if (!retryApply) return;
    setRetryApply(false);
    submit();
  }, [retryApply, submit]);

  if (loadState === 'loading') return <div className="gtap-msg">Loading the role…</div>;
  if (loadState === 'notfound') {
    return (
      <div className="gtap-msg">
        <h1 className="gtap-h1">Role not found</h1>
        <p className="gtap-sub">This role may have closed or the link is incomplete.</p>
        <a className="gtap-primary" href="/">Browse open roles</a>
      </div>
    );
  }
  if (loadState === 'error') {
    return (
      <div className="gtap-msg">
        <h1 className="gtap-h1">We could not load this role</h1>
        <p className="gtap-sub">Try again shortly.</p>
      </div>
    );
  }

  if (result) {
    return (
      <div className="gtap-done">
        <div className="gtap-tick" aria-hidden="true">✓</div>
        <h1 className="gtap-h1">
          {result.already_applied ? 'You have already applied' : 'Application received'}
        </h1>
        <p className="gtap-sub">
          {result.already_applied
            ? 'You applied to this role already. You can follow its progress in your portal.'
            : 'Thanks for applying to ' + (role.title || 'this role') + '. We will be in touch as it progresses.'}
        </p>
        <NextInterview />
        <LexaPrep roleRef={roleRef} roleTitle={role && role.title} />
        <a className="gtap-primary" href="/portal">View my applications</a>
      </div>
    );
  }

  const meta = [role.public_location, workPatternLabel(role.public_work_pattern), role.public_salary_text]
    .filter(Boolean).join('  ·  ');

  return (
    <div className="gtap-confirm">
      <p className="gtap-eyebrow">You are applying for</p>
      <h1 className="gtap-role">{role.title}</h1>
      {meta ? <p className="gtap-meta">{meta}</p> : null}
      {role.client_blurb ? <p className="gtap-blurb">{role.client_blurb}</p> : null}

      <label className="gtap-consent">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>
          I agree that Generative Talent Co (GTCO Ltd) may process my application and use
          artificial intelligence to help assess my suitability, as described in the
          candidate privacy notice.
        </span>
      </label>

      {needCv ? (
        <div className="gtpt-cvgate">
          <div className="gtpt-cvgate-h">Your CV first</div>
          <p className="gtpt-cvgate-p">
            We cannot put you forward on a name and an email alone. Add your CV and we will
            send this application straight away. PDF or Word, up to 10MB.
          </p>
          <label className="gtap-primary gtpt-cvgate-btn">
            {uploadingCv ? 'Uploading and applying' : 'Choose your CV'}
            <input type="file" accept=".pdf,.docx" style={{ display: 'none' }}
              disabled={uploadingCv}
              onChange={(e) => uploadCvThenApply(e.target.files && e.target.files[0])} />
          </label>
        </div>
      ) : null}

      {error ? <p className="gtap-error">{error}</p> : null}

      <button type="button" className="gtap-primary" disabled={!consent || submitting} onClick={submit}>
        {submitting ? 'Submitting…' : 'Submit application'}
      </button>

      <p className="gtap-fine">
        You are signed in. We use the email on your account to link your application.
      </p>
    </div>
  );
}

function workPatternLabel(v) {
  const m = { on_site: 'On-site', 'on-site': 'On-site', hybrid: 'Hybrid', remote: 'Remote' };
  return m[v] || (v || '');
}

// After applying to a role that interviews as part of applying: offer the
// interview straight away. The link is emailed either way, so "later" is safe.
function NextInterview() {
  const { getToken } = useAuth();
  const [st, setSt] = useState({ loading: true });
  const [later, setLater] = useState(false);
  const ref = decodeURIComponent((window.location.pathname.split('/apply/')[1] || '').split('/')[0] || '');
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const t = await getToken();
        const res = await fetch(API_BASE + '/api/portal/interviews/for-role/' + encodeURIComponent(ref), { method: 'POST', headers: { Authorization: 'Bearer ' + t } });
        const b = await res.json().catch(() => ({}));
        if (live) setSt({ interview: res.ok ? b.interview : null });
      } catch (e) { if (live) setSt({ interview: null }); }
    })();
    return () => { live = false; };
  }, [getToken, ref]);
  if (st.loading || !st.interview) return null;
  return (
    <div className="gtap-next">
      <div className="gtap-next-orb" aria-hidden="true" />
      <div className="gtap-next-body">
        <b>Next: a 20 minute interview with Nathan</b>
        <p>Nathan is our AI interviewer. It is a video call with live captions, and a person at Generative Talent Co reviews every interview. Have your camera and microphone ready and find a quiet spot.</p>
        {later ? (
          <p className="gtap-next-later">We have emailed you the link. It stays open for 14 days, whenever suits you.</p>
        ) : (
          <div className="gtap-next-acts">
            <a className="gtap-primary" href={'/interview/' + st.interview.token}>Start now</a>
            <button type="button" className="gtap-next-ghost" onClick={() => setLater(true)}>Do it later</button>
          </div>
        )}
      </div>
    </div>
  );
}

// P2-PORTAL: after applying, offer Lexa to prepare for this role's interview.
// Sold on what she does for this job, never on price. The link carries the
// role into the portal's Lexa tab, set up and ready.
function LexaPrep({ roleRef, roleTitle }) {
  if (!roleRef) return null;
  return (
    <div className="gtap-next gtap-lexa">
      <div className="gtlx-orb gtap-lexa-orb" aria-hidden="true" />
      <div className="gtap-next-body">
        <b>Get ready for the interview</b>
        <p>Practise the {roleTitle ? roleTitle + ' ' : ''}interview on video with Lexa, our AI coach. She builds the questions from this role and your CV, coaches you on each answer and writes up how to make it stronger.</p>
        <div className="gtap-next-acts">
          <a className="gtap-next-ghost gtap-lexa-go" href={'/?lexa=prep&role=' + encodeURIComponent(roleRef)}>Practise for this role</a>
        </div>
        <p className="gtap-lexa-fine">Optional and separate from your application. It never affects whether we put you forward.</p>
      </div>
    </div>
  );
}
